import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Crown, Check, ArrowLeft, Lightning, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const FREE_FEATURES = [
  "Up to 20 Paid Duty entries", "Up to 30 days of history", "Manual tracking",
];
const PRO_FEATURES = [
  "Unlimited tracking", "Email forwarding", "AI extraction", "Cloud backup",
  "PDF / Excel exports", "Notifications", "Charts & Analytics", "Priority support",
];

const STATUS_LABELS = {
  active: "Active", trialing: "Trial", past_due: "Past due",
  cancelled: "Cancelled", canceled: "Cancelled", none: "—",
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch { return null; }
}

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState("annual");

  const isPro = user?.subscription_plan === "Pro";

  const { data: limits, refetch: refetchLimits } = useQuery({
    queryKey: ["billing-limits"],
    queryFn: async () => (await api.get("/billing/limits")).data,
  });

  useEffect(() => {
    const status = params.get("status");
    if (status === "success") {
      toast.success("Payment complete! Your Pro upgrade is being activated. 🎉");
      // Poll a few times while the webhook lands.
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        const u = await refreshUser();
        refetchLimits();
        if (u?.subscription_plan === "Pro" || tries >= 6) clearInterval(iv);
      }, 2000);
      params.delete("status"); params.delete("session_id");
      setParams(params, { replace: true });
    } else if (status === "cancel") {
      toast.info("Checkout cancelled. You can upgrade anytime.");
      params.delete("status");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upgrade = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/stripe/create-checkout-session", { interval: billingInterval });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout.");
      setLoading(false);
    }
  };

  const manage = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/stripe/create-portal-session");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open the billing portal.");
      setLoading(false);
    }
  };

  const nextBilling = formatDate(limits?.current_period_end || user?.current_period_end);
  const statusLabel = STATUS_LABELS[user?.subscription_status] || user?.subscription_status || "—";

  return (
    <div className="animate-fade-up max-w-3xl" data-testid="billing-page">
      <button onClick={() => navigate("/settings")} data-testid="back-to-settings"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" weight="bold" /> Back to Settings
      </button>

      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center">
          <Crown className="h-6 w-6 text-primary" weight="fill" />
        </div>
        <div>
          <p className="overline text-primary">Billing</p>
          <h1 className="text-3xl font-black tracking-tight">Plan &amp; Subscription</h1>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="current-plan-badge">
        <span className="text-sm text-muted-foreground">Current plan:</span>
        <span className={`text-sm font-bold px-3 py-1 rounded-full ${
          isPro ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
          {isPro ? "Pro" : "Free"}
        </span>
        {isPro && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-secondary text-muted-foreground" data-testid="subscription-status">
            Status: {statusLabel}
          </span>
        )}
      </div>

      {isPro && nextBilling && (
        <Card className="p-4 mb-6 bg-card border-border" data-testid="next-billing">
          <p className="text-sm text-muted-foreground">
            {user?.subscription_status === "cancelled"
              ? "Your Pro access remains until"
              : "Next billing date"}
          </p>
          <p className="text-lg font-bold">{nextBilling}</p>
        </Card>
      )}

      {!isPro && limits?.paid_duty_limit != null && (
        <Card className="p-4 mb-6 bg-card border-border" data-testid="usage-meter">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Paid Duty entries used</span>
            <span className="font-semibold">{limits.paid_duty_used} / {limits.paid_duty_limit}</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${Math.min(100, (limits.paid_duty_used / limits.paid_duty_limit) * 100)}%` }} />
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card className={`p-6 border ${!isPro ? "border-primary/50 bg-primary/5" : "border-border bg-card"}`} data-testid="plan-free">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-5 w-5 text-muted-foreground" weight="bold" />
            <h3 className="text-lg font-black">Free</h3>
          </div>
          <p className="text-3xl font-black mb-4">$0<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
          <ul className="space-y-2 mb-6">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-emerald-400 shrink-0" weight="bold" /> {f}
              </li>
            ))}
          </ul>
          {!isPro && <p className="text-sm font-semibold text-primary text-center">Your current plan</p>}
        </Card>

        <Card className={`p-6 border relative overflow-hidden ${isPro ? "border-primary/50 bg-primary/5" : "border-primary/40 bg-card"}`} data-testid="plan-pro">
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/10 blur-2xl" />
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-5 w-5 text-primary" weight="fill" />
            <h3 className="text-lg font-black">Pro</h3>
          </div>

          {!isPro && limits?.annual_available && (
            <div className="inline-flex items-center rounded-full bg-secondary p-1 mb-3" data-testid="interval-toggle">
              <button
                onClick={() => setBillingInterval("monthly")}
                data-testid="interval-monthly"
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors duration-200 ${
                  billingInterval === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval("annual")}
                data-testid="interval-annual"
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors duration-200 ${
                  billingInterval === "annual" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                Annual · save 2 months
              </button>
            </div>
          )}

          {(() => {
            const cur = (limits?.currency || "cad").toUpperCase();
            if (isPro) {
              return (
                <div className="mb-4" data-testid="pro-price">
                  <p className="text-2xl font-black text-primary">Pro plan active</p>
                  <p className="text-xs text-muted-foreground mt-1">Manage or cancel anytime via the billing portal.</p>
                </div>
              );
            }
            const annual = billingInterval === "annual" && limits?.annual_available;
            const amount = annual ? (limits?.price_annual ?? 79.9) : (limits?.price ?? 7.99);
            return (
              <div className="mb-4" data-testid="pro-price">
                <p className="text-3xl font-black">
                  {money(amount, cur)}
                  <span className="text-sm font-normal text-muted-foreground">{annual ? "/yr" : "/mo"}</span>
                </p>
                {annual && (
                  <p className="text-xs text-emerald-400 mt-1" data-testid="annual-savings">
                    Just {money((limits?.price_annual ?? 79.9) / 12, cur)}/mo — save {money((limits?.price ?? 7.99) * 12 - (limits?.price_annual ?? 79.9), cur)}/yr
                  </p>
                )}
              </div>
            );
          })()}
          <ul className="space-y-2 mb-6">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm">
                <Lightning className="h-4 w-4 text-primary shrink-0" weight="fill" /> {f}
              </li>
            ))}
          </ul>
          {isPro ? (
            <Button variant="outline" onClick={manage} disabled={loading} data-testid="manage-subscription-button" className="w-full rounded-md font-semibold">
              {loading ? "Opening…" : "Manage Subscription"}
            </Button>
          ) : (
            <Button onClick={upgrade} disabled={loading} data-testid="upgrade-button" className="w-full rounded-md font-semibold">
              <Crown className="h-4 w-4 mr-1.5" weight="fill" /> {loading ? "Redirecting…" : "Upgrade to Pro"}
            </Button>
          )}
        </Card>
      </div>

      {limits && limits.stripe_configured === false && (
        <p className="text-xs text-amber-400/80 mt-4" data-testid="stripe-not-configured">
          Stripe keys are not configured yet — checkout will be available once billing is set up.
        </p>
      )}
    </div>
  );
}
